import { useState, useMemo, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import useOnClickOutside from 'use-onclickoutside';
import { Input } from '../Input';
import { usePopupPosition } from '../../hooks';
import { ButtonGroup } from '../ButtonGroup';
import Button from '../Button';
import Chip from '../Chip';
import { SearchIcon } from '../SearchBar/SearchIcon';
import type { CountryFilterMode, FilterValueProps } from '.';

type CountryFilterProps = {
  options: string[];
  selected: string[];
  mode: CountryFilterMode;
  onChange?: ({ selected, mode }: FilterValueProps) => void;
  onCancel: () => void;
  onApply: () => void;
  excludeOnly?: boolean;
};

export const CountryFilter: React.FC<CountryFilterProps> = ({
  options,
  selected,
  mode,
  onChange,
  onCancel,
  onApply,
  excludeOnly = false,
}) => {
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] =
    useState<string[]>(selected);
  const [viewMode, setViewMode] = useState<CountryFilterMode>(
    excludeOnly ? 'EXCLUDE' : mode
  );

  const refAnchor = useRef<any>(null);
  const { anchorRef, popupPosition, anchorPosition } = usePopupPosition();
  useOnClickOutside(refAnchor, onCancel);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(opt =>
      opt.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, options]);

  const toggleSelection = (country: string) => {
    setSelectedCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const handleClear = () => {
    setSelectedCountries([]);
    setSearch('');
  };

  const handleApply = () => {
    onChange?.({ selected: selectedCountries, mode: viewMode });
    onApply();
  };

  return (
    <div
      ref={node => {
        anchorRef(node as HTMLDivElement);
        refAnchor.current = node;
      }}
      style={{
        left: popupPosition?.style?.left ?? 0,
        top: popupPosition?.style?.top ?? 0,
        minWidth: anchorPosition.width ?? 300,
      }}
      className="shadow-lg bg-white p-4 rounded-xl w-[300px] flex flex-col gap-4 transition-all"
    >
      <div className="flex justify-between items-center border-b pb-4">
        <h2 className="font-semibold text-tiny sm:text-medium">
          Country Filter
        </h2>
        <div
          onClick={handleClear}
          className="text-main text-tiny sm:text-small hover:underline cursor-pointer"
        >
          Clear filter
        </div>
      </div>

      {!excludeOnly && (
        <ButtonGroup
          buttons={[
            {
              action: 'INCLUDE',
              label: 'Include selected',
              active: viewMode === 'INCLUDE',
            },
            {
              action: 'EXCLUDE',
              label: 'Exclude selected',
              active: viewMode === 'EXCLUDE',
            },
          ]}
          orientation="horizontal"
          buttonClassName="p-3"
          onClick={action => setViewMode(action as CountryFilterMode)}
        />
      )}
      <div className="flex flex-col">
        <Input
          type="text"
          size="small"
          label=""
          placeholder="Search country"
          value={search}
          prefix={
            <SearchIcon className="w-6 h-6 flex self-center mt-0 z-10 text-gray-400" />
          }
          labelClassName="text-secondary text-tiny sm:text-small font-semibold"
          inputClassName="pl-12 color-gray-400 rounded-full relative sm:text-small text-tiny"
          onChange={value => setSearch(value)}
          className="w-full rounded-full text-tiny sm:text-small focus:outline-primary"
        />
      </div>
      <div className="flex flex-wrap gap-2 border-b pb-4 max-h-24 sm:max-h-48 min-h-38 overflow-y-auto">
        {selectedCountries.map(country => (
          <Chip
            key={country}
            label={country}
            onClick={() => toggleSelection(country)}
          />
        ))}
      </div>

      <div className="flex flex-col gap-2 max-h-48 min-h-38 sm:max-h-72 overflow-y-auto">
        {filteredOptions
          .filter(country => !selectedCountries.includes(country))
          .map(country => (
            <label
              key={country}
              className="flex items-center justify-between px-2 sm:py-2 py-0 hover:bg-gray-100 rounded-md"
            >
              <span className="truncate text-tiny sm:text-medium">
                {country}
              </span>
              <div
                onClick={() => toggleSelection(country)}
                className="text-main hover:bg-main hover:text-contrast rounded-xs p-1"
              >
                <AddIcon
                  fontSize="large"
                  color="inherit"
                  className="cursor-pointer"
                />
              </div>
            </label>
          ))}
        {!filteredOptions.length && (
          <div className="text-gray-400 text-center text-medium sm:text-tiny py-4 cursor-pointer">
            No results
          </div>
        )}
      </div>

      <div className="flex gap-4 mt-1 border-t pt-4 ">
        <Button
          type="contrast"
          className="w-1/3 border border-gray-300 rounded-md py-2 text-medium sm:text-tiny flex-1"
          label="Cancel"
          onClick={onCancel}
        />

        <Button
          className="w-1/3 border bg-primary rounded-md py-2 text-medium sm:text-tiny flex-1"
          label="Apply"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};
